import { REQUEST_TIMEOUT_MS } from '../constants';
import { LLMProvider } from '../types';

/**
 * Backend configuration - this is configured in the chatbot library.
 * Replace with your actual backend endpoint.
 */
const BACKEND_ENDPOINT = process.env['CHATBOT_BACKEND_URL'] || 'https://rtai-api-test.recoverytrac.ai/rtchatbotapi/chat';

/**
 * Backend LLM provider that communicates with a custom backend API.
 * The backend is powered by Anthropic Claude and handles knowledge-constrained responses.
 */
export class Llm implements LLMProvider {
  /**
   * Sends a conversation to the custom backend and returns the assistant's response.
   * The backend API handles all knowledge base and system prompt logic.
   * @param messages - Array of message objects with role and content (user messages only)
   * @param signal - Optional AbortSignal for cancellation by the caller
   * @returns Promise resolving to the assistant's response string
   */
  async complete(
    messages: Array<{ role: string; content: string }>,
    signal?: AbortSignal,
  ): Promise<string> {
    // Create abort controller for timeout handling
    const abortController = new AbortController();
    
    // Set up timeout
    const timeoutId = setTimeout(() => {
      abortController.abort();
    }, REQUEST_TIMEOUT_MS);

    // Listen to caller's abort signal if provided
    const onAbort = () => {
      clearTimeout(timeoutId);
      abortController.abort();
    };
    signal?.addEventListener('abort', onAbort);

    // Prepare request body - send all message objects to backend
    const requestBody = {
      messages,
    };

    try {
      const response = await fetch(BACKEND_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        signal: abortController.signal,
      });

      if (!response.ok) {
        let errorMessage = response.statusText;
        try {
          const errorBody = await response.json();
          if (errorBody?.error?.message) {
            errorMessage = errorBody.error.message;
          }
        } catch {
          // Use statusText if body parsing fails
        }
        throw new Error(`Backend error: ${errorMessage}`);
      }

      const data = await response.json();

      // Handle different response formats
      if (typeof data === 'string') {
        return data;
      }

      if (data.answer) {
        return data.answer;
      }

      if (data.response) {
        return data.response;
      }

      if (data.message) {
        return data.message;
      }

      if (data.reply) {
        return data.reply;
      }

      throw new Error('Invalid response format from backend');
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Failed to communicate with backend');
    } finally {
      // Clean up timeout and abort listener
      clearTimeout(timeoutId);
      signal?.removeEventListener('abort', onAbort);
    }
  }
}
